import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import { Box, Typography, Chip, Button, IconButton, Collapse, Divider } from '@mui/material';
import Warning from '@mui/icons-material/Warning';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import CollapseIcon from '@mui/icons-material/KeyboardArrowUp';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import TrainingIcon from '@mui/icons-material/School';
import RocketIcon from '@mui/icons-material/RocketLaunch';
import { post } from '../utils/apiClient';

const DemoPill = ({ show, user }) => {
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);
  const [movingToTraining, setMovingToTraining] = useState(false);
  const { isDarkMode } = useDarkMode();
  const { logout, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  // Determine user state
  const hasClaimed = !!user?.claimed_org;
  const isSharedDemo = !hasClaimed && !!user?.demo_mode;
  const hasDemoCharacters = !!(user?.demo_character_ids && user.demo_character_ids.length > 0);

  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsExpanded(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape' && isExpanded) {
        event.preventDefault();
        setIsExpanded(false);
        buttonRef.current?.focus();
      }
    }
    
    if (isExpanded) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isExpanded]);

  const handleLogout = async () => {
    setIsExpanded(false);
    await logout('/login');
  };

  // For shared-demo users (pre-claim) the switcher lives on the /demo page.
  // Claimed users see the CharacterSwitcher rendered inline in this pill.
  const handleSwitchCharacter = () => {
    setIsExpanded(false);
    navigate('/demo');
  };

  const handleMoveToTraining = useCallback(async () => {
    setMovingToTraining(true);
    try {
      await post('/api/auth/training-mode', null, { credentials: 'include' });
      if (refreshAuth) {
        await refreshAuth();
      }
      window.location.reload();
    } catch {
      // Silently fail — user can retry
    } finally {
      setMovingToTraining(false);
    }
  }, [refreshAuth]);

  if (!show) return null;

  const characterType = user?.character_type
    || (hasClaimed ? (user?.name || user?.first_name || 'yourself') : (user?.role || 'demo'));
  const isImpersonating = Boolean(user?.impersonates_user_id);

  // Pill label: show DEMO for shared demo, show EXPLORE for forked org
  const pillLabel = isMobile ? 'D' : (hasClaimed ? 'EXPLORE' : 'DEMO');

  return (
    <div className="relative" ref={containerRef}>
      {/* Pill button */}
      <button
        ref={buttonRef}
        onClick={() => setIsExpanded(!isExpanded)}
        className={`inline-flex items-center gap-0.5 rounded-full font-semibold bg-amber-200 text-amber-900 dark:bg-amber-700 dark:text-amber-100 transition-opacity duration-150 cursor-pointer hover:bg-amber-300 dark:hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400 ${isMobile ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-[10px]'}`}
        role="button"
        aria-expanded={isExpanded}
        aria-haspopup="dialog"
        aria-label={hasClaimed ? 'Explore with demo characters - click to expand' : 'Demo mode info - click to expand'}
        title={hasClaimed ? 'Practice with demo characters to learn the EHR' : 'Click for demo info'}
        style={{ height: isMobile ? '22px' : 'var(--pill-height, 18px)' }}
      >
        {pillLabel}
        {isExpanded ? (
          <CollapseIcon sx={{ fontSize: isMobile ? 10 : 12 }} />
        ) : (
          <ExpandIcon sx={{ fontSize: isMobile ? 10 : 12 }} />
        )}
      </button>

      {/* Expanded info panel */}
      <Collapse in={isExpanded}>
        <Box
          sx={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '3.5rem' : 'auto',
            left: isMobile ? 'auto' : 0,
            right: isMobile ? '0.5rem' : 'auto',
            mt: isMobile ? 0 : 1,
            width: 'min(340px, calc(100vw - 16px))',
            minWidth: 0,
            maxWidth: 400,
            borderRadius: 2,
            boxShadow: 3,
            backgroundColor: isDarkMode ? '#1e293b' : '#e3f2fd',
            border: `1px solid ${isDarkMode ? '#334155' : '#1976d2'}`,
            zIndex: 1300,
            overflow: 'hidden',
          }}
        >
          {/* Header row */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            px: 2, 
            py: 1.5,
            borderBottom: `1px solid ${isDarkMode ? '#334155' : '#bbdefb'}`,
          }}>
            {hasClaimed ? (
              <SwapHoriz color="info" fontSize="small" />
            ) : (
              <Warning color="warning" fontSize="small" />
            )}
            <Typography variant="body2" sx={{ fontWeight: 'medium', color: isDarkMode ? '#f1f5f9' : 'inherit' }}>
              {hasClaimed
                ? 'Explore & Learn'
                : 'Demo Mode Active'}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setIsExpanded(false)}
              sx={{
                ml: 'auto',
                p: 0.5,
                color: isDarkMode ? '#94a3b8' : '#1976d2',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(25, 118, 210, 0.04)'
                }
              }}
              title="Collapse"
            >
              <CollapseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 1, color: isDarkMode ? '#cbd5e1' : 'text.secondary' }}>
              {hasClaimed && isImpersonating ? (
                <>Viewing as: <strong style={{ color: isDarkMode ? '#f1f5f9' : 'inherit' }}>{characterType.replace(/_/g, ' ')}</strong></>
              ) : hasClaimed ? (
                <>Exploring as: <strong style={{ color: isDarkMode ? '#f1f5f9' : 'inherit' }}>{characterType.replace(/_/g, ' ')} (you)</strong></>
              ) : (
                <>You&apos;re exploring as: <strong style={{ color: isDarkMode ? '#f1f5f9' : 'inherit' }}>{characterType.replace(/_/g, ' ')}</strong></>
              )}
            </Typography>
            
            {isSharedDemo && (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Chip 
                    label="7-Day Demo" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: '22px' }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : 'text.secondary' }}>
                    • Changes reset when your demo session ends
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : 'text.secondary' }}>
                    • Name your clinic to start customizing
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<RocketIcon />}
                  onClick={() => {
                    setIsExpanded(false);
                    window.dispatchEvent(new Event('open-claim-dialog'));
                  }}
                  sx={{
                    mb: 1.5,
                    width: '100%',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  Name Your Clinic
                </Button>
              </>
            )}

            {hasClaimed && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : 'text.secondary' }}>
                  Practice with demo characters to learn how the EHR works from different perspectives.
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : 'text.secondary' }}>
                  • No real PHI involved — safe to explore freely
                </Typography>
              </Box>
            )}

            {/* Claimed users get a Switch Character button that opens the
                full-page picker at /characters (matches demo-login UX). */}
            {hasClaimed && (
              <Box sx={{ display: 'flex', gap: 1, pt: 1, flexWrap: 'wrap', borderTop: `1px solid ${isDarkMode ? '#334155' : '#bbdefb'}` }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SwapHoriz />}
                  onClick={() => {
                    setIsExpanded(false);
                    navigate('/characters');
                  }}
                  sx={{
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                    textTransform: 'none',
                  }}
                >
                  Explore as Demo Character
                </Button>
              </Box>
            )}

            {/* Actions — only for shared-demo users; claimed users use the
                Switch Character button above */}
            {!hasClaimed && (
              <Box sx={{ display: 'flex', gap: 1, pt: 1, flexWrap: 'wrap', borderTop: `1px solid ${isDarkMode ? '#334155' : '#bbdefb'}` }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SwapHoriz />}
                  onClick={handleSwitchCharacter}
                  sx={{
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                    minHeight: '28px',
                    borderColor: isDarkMode ? '#64748b' : '#1976d2',
                    color: isDarkMode ? '#e2e8f0' : '#1976d2',
                    '&:hover': {
                      borderColor: isDarkMode ? '#94a3b8' : '#1565c0',
                      backgroundColor: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  Switch Character
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleLogout}
                  sx={{
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                    minHeight: '28px',
                    color: isDarkMode ? '#94a3b8' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  Exit
                </Button>
              </Box>
            )}

            {/* Move to Training Mode — only after fork, when demo characters exist */}
            {hasClaimed && hasDemoCharacters && (
              <>
                <Divider sx={{ my: 1.5, borderColor: isDarkMode ? '#334155' : '#bbdefb' }} />
                <Button
                  variant="text"
                  size="small"
                  startIcon={<TrainingIcon />}
                  onClick={handleMoveToTraining}
                  disabled={movingToTraining}
                  sx={{
                    fontSize: '0.75rem',
                    px: 1.5,
                    py: 0.5,
                    minHeight: '28px',
                    color: isDarkMode ? '#94a3b8' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(100, 116, 139, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  {movingToTraining ? 'Moving...' : 'Move Demo Characters to Training Mode'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Collapse>
    </div>
  );
};

export default DemoPill;
